import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';

/**
 * TEMU 类目树解析。
 * 兼容：
 * - 店铺 OpenAPI / 内部统一结构：category_id / category_name / children / last_category
 * - Admin 全局合并树：catId / catName / categoryDTOList / isLeaf|leaf
 */

function pickId(node) {
  if (!node || typeof node !== 'object') return '';
  const raw = node.category_id ?? node.catId ?? node.categoryId ?? node.id;
  return raw == null || raw === '' ? '' : String(raw);
}

function pickName(node) {
  if (!node || typeof node !== 'object') return '';
  const raw = node.category_name ?? node.catName ?? node.categoryName ?? node.name ?? node.pathLabel;
  return raw == null ? '' : String(raw).trim();
}

function pickChildren(node) {
  if (!node || typeof node !== 'object') return [];
  const raw = node.children ?? node.categoryDTOList ?? node.childList ?? node.subCategories;
  return Array.isArray(raw) ? raw : [];
}

function pickProductTypeId(node) {
  if (!node || typeof node !== 'object') return '';
  const raw = node.product_type_id ?? node.productTypeId;
  return raw == null || raw === '' ? '' : String(raw);
}

/** 将任意节点规范为级联组件使用的内部结构（递归）。 */
export function normalizeCategoryNode(node) {
  if (!node || typeof node !== 'object') return null;
  const categoryId = pickId(node);
  const categoryName = pickName(node);
  const childrenRaw = pickChildren(node);
  const children = childrenRaw
    .map((child) => normalizeCategoryNode(child))
    .filter(Boolean);
  const explicitLeaf = node.last_category === true
    || node.isLeaf === true
    || node.leaf === true;
  const explicitNonLeaf = node.last_category === false
    || node.isLeaf === false
    || node.leaf === false;
  // 显式非叶子且尚未懒加载子节点时，不能当成叶子
  const lastCategory = explicitLeaf || (!explicitNonLeaf && children.length === 0);
  return {
    ...node,
    category_id: categoryId,
    category_name: categoryName || (categoryId ? fixedUiTpl('分类 {0}', categoryId) : ''),
    product_type_id: pickProductTypeId(node),
    children,
    last_category: lastCategory,
    isLeaf: lastCategory,
    leaf: lastCategory,
  };
}

export function normalizeCategoryTree(raw) {
  if (!raw) return [];
  let list = raw;
  if (!Array.isArray(list)) {
    if (Array.isArray(raw.data)) list = raw.data;
    else if (Array.isArray(raw.categories)) list = raw.categories;
    else if (Array.isArray(raw.categoryDTOList)) list = raw.categoryDTOList;
    else if (Array.isArray(raw.result?.categoryDTOList)) list = raw.result.categoryDTOList;
    else if (Array.isArray(raw.items)) list = raw.items;
    else return [];
  }
  return list.map((node) => normalizeCategoryNode(node)).filter(Boolean);
}

export function getCategoryRoots(raw) {
  return normalizeCategoryTree(raw);
}

export function isLeafCategoryNode(node) {
  if (!node || typeof node !== 'object') return false;
  if (node.last_category === true || node.isLeaf === true || node.leaf === true) return true;
  if (node.last_category === false || node.isLeaf === false || node.leaf === false) return false;
  const children = pickChildren(node);
  return children.length === 0;
}

/** 把子节点挂到树中指定节点（不可变更新）。 */
export function attachCategoryChildren(roots, parentId, childrenRaw) {
  const pid = String(parentId ?? '').trim();
  if (!pid) return roots;
  const children = normalizeCategoryTree(childrenRaw);
  const patch = (nodes) => {
    if (!Array.isArray(nodes)) return nodes;
    return nodes.map((node) => {
      if (!node) return node;
      if (pickId(node) === pid) {
        return {
          ...node,
          children,
          last_category: false,
          isLeaf: false,
          leaf: false,
        };
      }
      const nested = pickChildren(node);
      if (!nested.length) return node;
      return { ...node, children: patch(nested) };
    });
  };
  return patch(getCategoryRoots(roots));
}

export function findCategoryNodeByPath(roots, pathIds) {
  const path = [];
  let nodes = roots;
  for (const id of pathIds) {
    const node = nodes.find((item) => pickId(item) === String(id));
    if (!node) break;
    path.push(node);
    nodes = pickChildren(node);
  }
  return path;
}

/** 按末级（或任意节点）类目 ID 反查从根到该节点的 pathIds */
export function findCategoryPathIds(raw, categoryId) {
  const target = String(categoryId ?? '').trim();
  if (!target) return [];
  const roots = getCategoryRoots(raw);

  const walk = (nodes, ancestors) => {
    if (!Array.isArray(nodes)) return null;
    for (const node of nodes) {
      if (!node || typeof node !== 'object') continue;
      const id = pickId(node);
      const next = [...ancestors, id];
      if (id && id === target) return next;
      const found = walk(pickChildren(node), next);
      if (found) return found;
    }
    return null;
  };

  return walk(roots, []) || [];
}

export function buildCategoryColumns(roots, pathIds) {
  const columns = [];
  let nodes = roots;
  if (nodes.length > 0) {
    columns.push(nodes);
  }
  for (const id of pathIds) {
    const node = nodes.find((item) => pickId(item) === String(id));
    if (!node) break;
    const children = pickChildren(node);
    if (children.length > 0) {
      columns.push(children);
      nodes = children;
    } else {
      break;
    }
  }
  return columns;
}

/** 扁平化末级类目，供发品页下拉选择 */
export function flattenLeafCategories(raw) {
  const roots = getCategoryRoots(raw);
  const leaves = [];

  const walk = (nodes, ancestors) => {
    if (!Array.isArray(nodes)) return;
    for (const node of nodes) {
      if (!node || typeof node !== 'object') continue;
      const path = [...ancestors, node];
      if (isLeafCategoryNode(node)) {
        const label = path.map((n) => pickName(n) || n.category_name).filter(Boolean).join(' / ');
        const categoryId = pickId(node);
        leaves.push({
          categoryId,
          productTypeId: pickProductTypeId(node) || categoryId,
          label: label || (categoryId ? fixedUiTpl('分类 {0}', categoryId) : fixedUi('未命名类目')),
          pathIds: path.map((n) => pickId(n)).filter(Boolean),
        });
      } else {
        walk(pickChildren(node), path);
      }
    }
  };

  walk(roots, []);
  return leaves;
}

/**
 * 模板列表「类目」展示：优先完整一级→末级路径，绝不回退成裸数字 ID。
 * @param {{ categoryPathLabel?: string, categoryId?: string|number, productTypeId?: string|number }} row
 * @param {Map<string, string>|Record<string, string>|null} [labelById] categoryId → 完整路径名
 */
export function formatTemplateCategoryLabel(row, labelById = null) {
  const id = String(row?.categoryId || row?.productTypeId || '').trim();
  let fromMap = '';
  if (id) {
    if (labelById instanceof Map) {
      fromMap = String(labelById.get(id) || '').trim();
    } else if (labelById && typeof labelById === 'object') {
      fromMap = String(labelById[id] || '').trim();
    }
  }
  const stored = String(row?.categoryPathLabel || '').trim();
  // 类目树完整路径优先于库里可能只存了末级名的 categoryPathLabel
  if (isFullerCategoryPath(fromMap, stored)) {
    return fromMap;
  }
  if (stored && !/^\d+$/.test(stored)) {
    return stored;
  }
  if (fromMap && !/^\d+$/.test(fromMap)) {
    return fromMap;
  }
  return '';
}

/** 路径段数（按 / 或 > 拆）；用于判断是否比末级名更完整 */
export function categoryPathDepth(label) {
  const parts = String(label || '')
    .split(/\s*\/\s*|\s*>\s*|\s*›\s*/)
    .map((p) => p.trim())
    .filter(Boolean);
  return parts.length;
}

export function isFullerCategoryPath(candidate, current) {
  const c = String(candidate || '').trim();
  if (!c || /^\d+$/.test(c)) return false;
  const cur = String(current || '').trim();
  if (!cur || /^\d+$/.test(cur)) return true;
  return categoryPathDepth(c) > categoryPathDepth(cur);
}
